import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ArrowLeft, Wrench, History, CalendarClock, Eye, Play, AlertTriangle, ShieldAlert, Activity, Copy, Loader2 } from 'lucide-react';
import { UserMinus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Each job's metadata + endpoint mapping.
const JOBS = [
  {
    id: 'normalize-legacy-history',
    title: 'Normalize Legacy Surveyor History',
    description:
      'Rewrites old `asked_by_history` entries that were saved with surveyor_id="unknown" so they use the canonical "unknown-{internal|external}-active" sentinel and carry the correct name. Lets filters like "asked by Unknown External" match those rows.',
    icon: History,
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    endpoint: '/api/admin/maintenance/normalize-legacy-history',
    countKey: 'entries_normalized',
    countLabel: 'entries to normalize',
    countLabelShort: 'Entries normalized',
  },
  {
    id: 'backfill-undated-history',
    title: 'Backfill Undated History',
    description:
      'Fills any `asked_by_history` entry with a missing exam_month using the entry\'s added_at month/year (preferred) or the current month/year. Ensures undated rows group cleanly in the Question History modal.',
    icon: CalendarClock,
    color: 'text-sky-700',
    bgColor: 'bg-sky-100',
    endpoint: '/api/admin/maintenance/backfill-undated-history',
    countKey: 'entries_filled',
    countLabel: 'entries to fill',
    countLabelShort: 'Entries filled',
  },
  {
    id: 'backfill-empty-history',
    title: 'Backfill Empty Sitting History',
    description:
      'Seeds `asked_by_history` for approved questions where the array is missing/empty but a surveyor + asked count exist on the question. One synthesized sitting per question so the invariant `asked = sum(sittings)` holds. Idempotent — safe to re-run.',
    icon: History,
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    endpoint: '/api/admin/maintenance/backfill-empty-history',
    countKey: 'entries_seeded',
    countLabel: 'sittings to seed',
    countLabelShort: 'Sittings seeded',
  },
  {
    id: 'demote-expired-subscriptions',
    title: 'Demote Expired Subscriptions',
    description:
      'Rewrites users whose paid subscription has lapsed back to the free plan (clears plan_expires_at). Same logic also runs unattended daily at 02:00 — this card is for on-demand triggering.',
    icon: UserMinus,
    color: 'text-rose-700',
    bgColor: 'bg-rose-100',
    endpoint: '/api/admin/maintenance/demote-expired-subscriptions',
    countKey: 'entries_demoted',
    countLabel: 'users to demote',
    countLabelShort: 'Users demoted',
  },
];

const DataMaintenancePage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;

  // Currently active job — drives the preview / confirm dialog.
  // Shape: { job, status: 'previewing'|'preview-ready'|'applying'|null, preview: <response>, error: string }
  const [active, setActive] = useState(null);

  // ── Read-only audit state ────────────────────────────────────────────
  // The Audit History Integrity card is a separate flow from JOBS: no
  // preview/apply cycle, just a single GET that dumps the current state
  // of asked_by_history integrity. Used to diagnose why the two backfill
  // scripts might be reporting "0 entries filled" even when undated
  // entries are visible in the History modal.
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditError, setAuditError] = useState('');

  const runAudit = async () => {
    setAuditRunning(true);
    setAuditError('');
    setAuditResult(null);
    try {
      const resp = await axios.get(
        `${API}/api/admin/maintenance/audit-history-integrity`,
        { headers: authHeaders() }
      );
      setAuditResult(resp.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Audit failed';
      setAuditError(msg);
      toast.error(`Audit failed: ${msg}`);
    } finally {
      setAuditRunning(false);
    }
  };

  const copyAudit = async () => {
    if (!auditResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(auditResult, null, 2));
      toast.success('Audit JSON copied to clipboard');
    } catch (_e) {
      toast.error('Copy failed — please select and copy manually.');
    }
  };

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const runPreview = async (job) => {
    setActive({ job, status: 'previewing', preview: null, error: null });
    try {
      const resp = await axios.post(
        `${API}${job.endpoint}?dry_run=true`,
        null,
        { headers: authHeaders() }
      );
      setActive({ job, status: 'preview-ready', preview: resp.data, error: null });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Preview failed';
      toast.error(`Preview failed: ${msg}`);
      setActive(null);
    }
  };

  const runApply = async () => {
    if (!active?.job) return;
    setActive(a => ({ ...a, status: 'applying' }));
    try {
      const resp = await axios.post(
        `${API}${active.job.endpoint}?dry_run=false`,
        null,
        { headers: authHeaders() }
      );
      const data = resp.data || {};
      const count = data[active.job.countKey] ?? 0;
      const affected = data.questions_affected ?? 0;
      toast.success(
        `${active.job.title}: ${count} ${active.job.countLabelShort.toLowerCase()} across ${affected} question${affected !== 1 ? 's' : ''}.`,
        { duration: 6000 }
      );
      setActive(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Apply failed';
      toast.error(`Apply failed: ${msg}`);
      setActive(a => ({ ...a, status: 'preview-ready' }));
    }
  };

  return (
    <Layout>
      <div className="page-transition" data-testid="data-maintenance-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/tools')}
            className="text-gray-600"
            data-testid="data-maintenance-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Tools
          </Button>
        </div>

        <Card className="mb-8 border-l-4 border-l-[#6366F1] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#6366F1]/5 to-[#0A7EA4]/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#6366F1] rounded-lg">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-3xl text-[#6366F1]">Data Maintenance</CardTitle>
                <CardDescription className="text-base mt-1">
                  One-off jobs to repair or normalize legacy data. Always run Preview before Apply.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Non-super-admin gate */}
        {!isSuperAdmin && (
          <Card className="mb-6 border-l-4 border-l-amber-500 bg-amber-50" data-testid="super-admin-warning">
            <CardContent className="pt-6 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <strong>Super-admin only.</strong> These jobs read and modify data across all questions.
                You are signed in as a regular admin, so the buttons below will return a 403 on the server.
                Sign in as the env-whitelisted super-admin to run them.
              </div>
            </CardContent>
          </Card>
        )}

        {/* Read-only Audit card — sits above the JOBS grid so users see
            the diagnostic option first before touching writes. */}
        <Card
          className="mb-6 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white"
          data-testid="maintenance-card-audit-history-integrity"
        >
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Activity className="w-6 h-6 text-indigo-700" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl text-indigo-900">Audit History Integrity</CardTitle>
                <CardDescription className="text-sm mt-1 leading-relaxed">
                  Read-only diagnostic. Scans every question&apos;s
                  <code className="mx-1 px-1 py-0.5 bg-white rounded text-xs">asked_by_history</code>
                  array and reports any entries with missing dates or missing surveyor IDs,
                  broken down by question status. Zero writes — safe to run anytime.
                  Use before or after the backfill jobs to see the exact shape of the data.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={runAudit}
                disabled={!isSuperAdmin || auditRunning}
                className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                data-testid="maintenance-audit-run-btn"
              >
                {auditRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                {auditRunning ? 'Running…' : 'Run Audit'}
              </Button>
              {auditResult && (
                <Button
                  onClick={copyAudit}
                  variant="outline"
                  className="gap-2"
                  data-testid="maintenance-audit-copy-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy JSON
                </Button>
              )}
            </div>

            {auditError && (
              <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">
                {auditError}
              </div>
            )}

            {auditResult && (
              <div className="mt-4 space-y-3" data-testid="maintenance-audit-result">
                {/* Summary at a glance */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-white rounded border border-gray-200">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Scanned</div>
                    <div className="text-xl font-semibold">{auditResult.counters?.scanned ?? 0}</div>
                  </div>
                  <div className="p-3 bg-white rounded border border-gray-200">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">With history</div>
                    <div className="text-xl font-semibold">{auditResult.counters?.total_with_history ?? 0}</div>
                  </div>
                  <div className="p-3 bg-white rounded border border-amber-300">
                    <div className="text-xs text-amber-700 uppercase tracking-wide">Undated entries</div>
                    <div className="text-xl font-semibold text-amber-800">
                      {auditResult.counters?.total_undated_entries ?? 0}
                    </div>
                    <div className="text-xs text-amber-700 mt-0.5">
                      in {auditResult.counters?.questions_with_undated_entries ?? 0} questions
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded border border-rose-300">
                    <div className="text-xs text-rose-700 uppercase tracking-wide">Missing surveyor</div>
                    <div className="text-xl font-semibold text-rose-800">
                      {auditResult.counters?.total_missing_surveyor_entries ?? 0}
                    </div>
                    <div className="text-xs text-rose-700 mt-0.5">
                      in {auditResult.counters?.questions_with_missing_surveyor_entries ?? 0} questions
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white rounded border border-gray-200 text-xs">
                  <div className="text-gray-500 uppercase tracking-wide mb-1">Approved rows with NO history at all</div>
                  <div className="text-lg font-semibold">{auditResult.empty_history_approved_count ?? 0}</div>
                  <div className="text-gray-500 mt-1">
                    (These are candidates for Backfill Empty Sitting History.)
                  </div>
                </div>

                {/* Raw JSON for pasting to support / debugging */}
                <details className="bg-white border border-gray-200 rounded" data-testid="maintenance-audit-raw">
                  <summary className="p-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Full JSON (paste to support if asked)
                  </summary>
                  <pre className="p-3 text-xs overflow-x-auto bg-gray-50 border-t border-gray-200 max-h-[400px] overflow-y-auto">
                    {JSON.stringify(auditResult, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Jobs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {JOBS.map((job) => (
            <Card
              key={job.id}
              className="border-2 border-gray-200 hover:border-[#6366F1] transition-all"
              data-testid={`maintenance-card-${job.id}`}
            >
              <CardHeader>
                <div className={`p-3 ${job.bgColor} rounded-lg mb-4 w-fit`}>
                  <job.icon className={`w-8 h-8 ${job.color}`} />
                </div>
                <CardTitle className="text-xl">{job.title}</CardTitle>
                <CardDescription className="text-sm mt-2 leading-relaxed">
                  {job.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex gap-2 flex-wrap pt-2">
                <Button
                  variant="outline"
                  onClick={() => runPreview(job)}
                  disabled={!isSuperAdmin || (active?.job?.id === job.id && active?.status === 'previewing')}
                  data-testid={`maintenance-preview-${job.id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {active?.job?.id === job.id && active?.status === 'previewing' ? 'Loading…' : 'Preview (Dry Run)'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Preview / Confirm Dialog */}
        <AlertDialog
          open={!!active && (active.status === 'preview-ready' || active.status === 'applying')}
          onOpenChange={(open) => { if (!open && active?.status !== 'applying') setActive(null); }}
        >
          <AlertDialogContent className="max-w-2xl" data-testid="maintenance-preview-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{active?.job?.title}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4 text-sm text-gray-700">
                  {active?.preview && (
                    <>
                      <div className="rounded-md bg-gray-50 border border-gray-200 p-3 grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-xs uppercase text-gray-500">Scanned</div>
                          <div className="text-lg font-semibold">{active.preview.scanned ?? 0}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase text-gray-500">Questions affected</div>
                          <div className="text-lg font-semibold">{active.preview.questions_affected ?? 0}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-xs uppercase text-gray-500">
                            {active.job.countLabelShort}
                          </div>
                          <div className="text-lg font-semibold text-[#0A7EA4]">
                            {active.preview[active.job.countKey] ?? 0}
                          </div>
                        </div>
                      </div>

                      {(active.preview[active.job.countKey] ?? 0) === 0 ? (
                        <div className="flex items-start gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                          <span className="font-medium">No changes needed.</span>
                          Data is already clean for this job.
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                              Applying will modify <strong>{active.preview.questions_affected}</strong> question{active.preview.questions_affected !== 1 ? 's' : ''}.
                              This action is idempotent — running it twice is safe.
                            </span>
                          </div>

                          {Array.isArray(active.preview.samples) && active.preview.samples.length > 0 && (
                            <div>
                              <div className="text-xs uppercase text-gray-500 mb-1">
                                Sample affected questions ({active.preview.samples.length})
                              </div>
                              <div className="max-h-40 overflow-auto border border-gray-200 rounded-md p-2 bg-gray-50 text-xs font-mono">
                                {active.preview.samples.map((s, idx) => (
                                  <div key={idx} className="py-1 border-b border-gray-200 last:border-0 break-all">
                                    {s.question_id}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={active?.status === 'applying'}
                data-testid="maintenance-dialog-cancel"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); runApply(); }}
                disabled={active?.status === 'applying' || (active?.preview?.[active?.job?.countKey] ?? 0) === 0}
                className="bg-[#0A7EA4] hover:bg-[#086283]"
                data-testid="maintenance-dialog-apply"
              >
                <Play className="w-4 h-4 mr-2" />
                {active?.status === 'applying' ? 'Applying…' : 'Apply'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default DataMaintenancePage;
